<template>
  <div data-testid="seasons-editor">
    <div class="se-months text-caption text--secondary">
      <span v-for="(m, idx) in monthLabels" :key="m" class="se-months__label">
        <span class="se-months__full">{{ m }}</span>
        <span class="se-months__letter">{{ monthLetters[idx] }}</span>
      </span>
    </div>
    <div ref="band" class="se-band" data-testid="seasons-band" @click="onBandClick">
      <div v-for="(iv, k) in bandIntervals" :key="k" class="se-interval"
        :class="{ 'se-interval--conflict': iv.conflict, 'se-interval--clickable': !disabled }"
        :style="{ left: iv.left + '%', width: iv.width + '%', background: iv.color }"
        :data-testid="`seasons-band-interval-${k}`" :title="iv.label"
        @click.stop="openEditor(iv.origIndex)"></div>
      <span v-if="!bandIntervals.length" class="se-band__empty text-caption text--secondary">
        {{ $t("companies.base_prices_time") }}
      </span>
    </div>
    <div class="text-caption text--secondary mb-2">{{ $t("companies.seasons_band_hint") }}</div>

    <div class="se-chips">
      <v-chip v-for="entry in sortedSeasons" :key="entry.origIndex" class="se-chip"
        :class="{ 'se-chip--conflict': conflictIndexes.has(entry.origIndex), 'se-chip--active': editorIndex === entry.origIndex }"
        outlined data-testid="season-row" :data-start="dateAttr(entry.season, 'start')"
        :data-end="dateAttr(entry.season, 'end')" @click="openEditor(entry.origIndex)">
        <span class="se-chip__color" :style="{ background: colorForIndex(entry.origIndex) }"></span>
        <v-icon v-if="conflictIndexes.has(entry.origIndex)" x-small color="error" class="mr-1"
          data-testid="season-conflict" :title="$t('errors.seasons_cross')">mdi-alert-circle</v-icon>
        <v-icon v-else-if="!isParseable(entry.season)" x-small color="warning" class="mr-1"
          data-testid="season-unparseable" :title="$t('companies.season_unparseable')">mdi-alert</v-icon>
        <span>{{ seasonLabel(entry.season) }}</span>
        <v-icon v-if="!disabled && (canDeleteSaved || !entry.season.id)" small class="ml-1" data-testid="season-delete"
          :aria-label="$t('custom.delete')" @click.stop="$emit('delete', entry.origIndex)">mdi-close</v-icon>
      </v-chip>
      <v-btn v-if="!disabled" small text color="primary" data-testid="season-add" @click="addSeason">
        <v-icon small class="mr-1">mdi-plus</v-icon>{{ $t("companies.add_season") }}
      </v-btn>
    </div>

    <v-card v-if="editorSeason && !disabled" outlined class="se-editor mt-2 pa-3"
      :class="{ 'se-editor--conflict': conflictIndexes.has(editorIndex) }" data-testid="season-editor">
      <div class="se-editor__row">
        <span class="se-chip__color mr-2" :style="{ background: colorForIndex(editorIndex) }"></span>
        <v-select :value="dayValue(editorSeason.start_date_day)" :items="days" dense hide-details
          class="se-select se-select--day" data-testid="season-start-day"
          @change="onFieldChange('start_date_day', $event)"></v-select>
        <v-select :value="monthValue(editorSeason.start_date_month)" :items="months" item-text="text"
          item-value="number" dense hide-details class="se-select" data-testid="season-start-month"
          @change="onFieldChange('start_date_month', $event)"></v-select>
        <span class="mx-2">—</span>
        <v-select :value="dayValue(editorSeason.end_date_day)" :items="days" dense hide-details
          class="se-select se-select--day" data-testid="season-end-day"
          @change="onFieldChange('end_date_day', $event)"></v-select>
        <v-select :value="monthValue(editorSeason.end_date_month)" :items="months" item-text="text"
          item-value="number" dense hide-details class="se-select" data-testid="season-end-month"
          @change="onFieldChange('end_date_month', $event)"></v-select>
        <v-spacer></v-spacer>
        <v-btn small text data-testid="season-editor-close" @click="editorIndex = null">
          {{ $t("custom.close") }}
        </v-btn>
      </div>
      <div v-if="conflictIndexes.has(editorIndex)" class="text-caption error--text mt-1">
        {{ $t("errors.seasons_cross") }}
      </div>
    </v-card>
  </div>
</template>

<script>
import moment from "moment";
import {
  findSeasonConflicts,
  seasonDayOfYearIntervals,
} from "../users/seasonHelpers";

// Приглушённая палитра, читаемая в светлой и тёмной темах (OQ-01 default).
const PALETTE = [
  "rgba(33, 150, 243, 0.55)",
  "rgba(76, 175, 80, 0.55)",
  "rgba(255, 152, 0, 0.6)",
  "rgba(156, 39, 176, 0.5)",
  "rgba(0, 188, 212, 0.55)",
  "rgba(233, 30, 99, 0.5)",
  "rgba(121, 85, 72, 0.55)",
  "rgba(96, 125, 139, 0.55)",
];
const YEAR_DAYS = 365;
const MONTH_LAST_DAY = [null, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const DEFAULT_SEASON_DAYS = 14;

export default {
  name: "SeasonsEditor",
  props: {
    value: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    // Удаление сохранённого сезона (с id) — только тем, кому его разрешает сервер; несохранённую строку убрать может любой.
    // Закрыто по умолчанию: потребитель, забывший проп, не откроет удаление сезона с ценами всех машин.
    canDeleteSaved: { type: Boolean, default: false },
  },
  data() {
    return {
      editorIndex: null,
    };
  },
  computed: {
    days() {
      return Array.from({ length: 31 }, (_, i) => i + 1);
    },
    months() {
      return moment.months().map((text, idx) => ({
        text,
        number: String(idx + 1).padStart(2, "0"),
      }));
    },
    monthLabels() {
      return moment.monthsShort();
    },
    monthLetters() {
      return moment.months().map((m) => m.charAt(0).toUpperCase());
    },
    conflictIndexes() {
      const set = new Set();
      findSeasonConflicts(this.value).forEach(({ a, b }) => {
        set.add(a);
        set.add(b);
      });
      return set;
    },
    sortedSeasons() {
      return this.value
        .map((season, origIndex) => ({
          season,
          origIndex,
          intervals: seasonDayOfYearIntervals(season),
        }))
        .sort((x, y) => {
          const xs = x.intervals ? x.intervals[0].startDoy : 999;
          const ys = y.intervals ? y.intervals[0].startDoy : 999;
          return xs - ys;
        });
    },
    // Порядок по началу сезона — для стабильного назначения цветов.
    colorOrder() {
      return this.sortedSeasons.reduce((acc, entry, pos) => {
        if (entry.intervals) acc[entry.origIndex] = pos;
        return acc;
      }, {});
    },
    bandIntervals() {
      const result = [];
      this.value.forEach((season, origIndex) => {
        const intervals = seasonDayOfYearIntervals(season);
        if (!intervals) return;
        intervals.forEach(({ startDoy, endDoy }) => {
          result.push({
            origIndex,
            left: ((startDoy - 1) / YEAR_DAYS) * 100,
            width: ((endDoy - startDoy + 1) / YEAR_DAYS) * 100,
            color: this.colorForIndex(origIndex),
            conflict: this.conflictIndexes.has(origIndex),
            label: this.seasonLabel(season),
          });
        });
      });
      return result;
    },
    editorSeason() {
      return this.editorIndex === null ? null : this.value[this.editorIndex] || null;
    },
  },
  methods: {
    isParseable(season) {
      return seasonDayOfYearIntervals(season) !== null;
    },
    dayValue(day) {
      return parseInt(day, 10) || null;
    },
    monthValue(month) {
      const m = parseInt(month, 10);
      return m ? String(m).padStart(2, "0") : null;
    },
    dateAttr(season, side) {
      return `${season[`${side}_date_day`]}.${this.monthValue(season[`${side}_date_month`]) || "??"}`;
    },
    colorForIndex(i) {
      const pos = this.colorOrder[i];
      return pos === undefined ? "rgba(128,128,128,0.4)" : PALETTE[pos % PALETTE.length];
    },
    seasonLabel(season) {
      const short = this.monthLabels;
      const sm = parseInt(season.start_date_month, 10);
      const em = parseInt(season.end_date_month, 10);
      if (!sm || !em) return `${season.start_date_day}.?? — ${season.end_date_day}.??`;
      return `${season.start_date_day} ${short[sm - 1]} — ${season.end_date_day} ${short[em - 1]}`;
    },
    openEditor(origIndex) {
      if (this.disabled) return;
      this.editorIndex = origIndex;
    },
    onFieldChange(field, val) {
      if (this.editorIndex === null) return;
      const next = this.value.slice();
      next[this.editorIndex] = { ...next[this.editorIndex], [field]: val };
      this.$emit("input", next);
    },
    addSeason() {
      this.appendSeason({ start_date_day: 1, start_date_month: "01", end_date_day: 14, end_date_month: "01" });
    },
    appendSeason(season) {
      this.$emit("input", [...this.value, season]);
      this.editorIndex = this.value.length;
    },
    onBandClick(event) {
      if (this.disabled || !this.$refs.band) return;
      const rect = this.$refs.band.getBoundingClientRect();
      if (!rect.width) return;
      const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
      this.createSeasonAt(Math.max(1, Math.round(ratio * YEAR_DAYS)));
    },
    // Новый сезон в свободном окне вокруг doy; конец клампится к соседу слева-справа.
    createSeasonAt(doy) {
      if (this.disabled) return;
      const occupied = [];
      this.value.forEach((season) => {
        (seasonDayOfYearIntervals(season) || []).forEach((iv) => occupied.push(iv));
      });
      if (occupied.some((iv) => doy >= iv.startDoy && doy <= iv.endDoy)) return;
      const nextStart = occupied
        .map((iv) => iv.startDoy)
        .filter((s) => s > doy)
        .sort((a, b) => a - b)[0];
      const end = Math.min(doy + DEFAULT_SEASON_DAYS - 1, (nextStart || YEAR_DAYS + 1) - 1, YEAR_DAYS);
      const [sd, sm] = this.doyToDayMonth(doy);
      const [ed, em] = this.doyToDayMonth(end);
      this.appendSeason({ start_date_day: sd, start_date_month: sm, end_date_day: ed, end_date_month: em });
    },
    doyToDayMonth(doy) {
      let rest = doy;
      for (let m = 1; m <= 12; m++) {
        if (rest <= MONTH_LAST_DAY[m]) return [rest, String(m).padStart(2, "0")];
        rest -= MONTH_LAST_DAY[m];
      }
      return [31, "12"];
    },
  },
};
</script>

<style scoped>
.se-months {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  text-align: center;
  margin-bottom: 2px;
}
.se-months__label {
  overflow: hidden;
  white-space: nowrap;
}
.se-months__letter {
  display: none;
}
@media (max-width: 599px) {
  .se-months__full {
    display: none;
  }
  .se-months__letter {
    display: inline;
  }
  .se-band {
    height: 38px !important;
  }
}
.se-band {
  position: relative;
  height: 30px;
  border: 1px solid rgba(128, 128, 128, 0.45);
  border-radius: 6px;
  background: rgba(128, 128, 128, 0.12);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: copy;
}
.se-interval {
  position: absolute;
  top: 0;
  bottom: 0;
}
.se-interval--clickable {
  cursor: pointer;
}
.se-interval--conflict {
  outline: 2px solid #f44336;
  outline-offset: -2px;
}
.se-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}
.se-chip {
  cursor: pointer;
}
.se-chip--conflict {
  border-color: #f44336 !important;
  background: rgba(244, 67, 54, 0.08) !important;
}
.se-chip--active {
  border-color: #2196f3 !important;
}
.se-chip__color {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  margin-right: 6px;
}
.se-editor {
  max-width: 720px;
}
.se-editor--conflict {
  border-color: #f44336 !important;
}
.se-editor__row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.se-select {
  max-width: 150px;
  margin-right: 8px;
}
.se-select--day {
  max-width: 64px;
}
</style>
